<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize, useWindowSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConfirmActionDialog from 'dashboard/components-next/dialog/ConfirmActionDialog.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  conversationListPageURL,
  frontendURL,
} from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { width: windowWidth } = useWindowSize();
const { isAWebWidgetInbox } = useInbox();
const isDropdownOpen = ref(false);
const showCopiedToast = ref(false);
const isClosingConversation = ref(false);
const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const copyConversationLink = () => {
  const url = window.location.href;
  navigator.clipboard.writeText(url).then(() => {
    showCopiedToast.value = true;
    setTimeout(() => {
      showCopiedToast.value = false;
    }, 2000);
  });
};

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const isQaMonitorRoute = computed(() => {
  const routeName = String(route?.name || '');
  const routePath = String(route?.path || '');
  return (
    routeName.startsWith('qa_monitor') || routePath.includes('/qa-monitor')
  );
});

const backButtonUrl = computed(() => {
  if (isQaMonitorRoute.value) {
    const qaInboxId = route?.params?.inbox_id;

    if (qaInboxId) {
      return frontendURL(
        `accounts/${accountId.value}/qa-monitor/inbox/${qaInboxId}`
      );
    }

    return frontendURL(`accounts/${accountId.value}/qa-monitor`);
  }

  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
    conversation_through_vip: 'vip',
    conversation_through_wlab_ai_agent_all: 'wlab_ai_agent_all',
    conversation_through_wlab_ai_agent_resolved: 'wlab_ai_agent_resolved',
    conversation_through_wlab_ai_agent_handoff: 'wlab_ai_agent_handoff',
    conversation_through_wlab_ai_agent_pending: 'wlab_ai_agent_pending',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

// Not contact.identifier: that value is redacted to "wl***45" on its way to the
// dashboard for custom-role agents without contact_manage. custom_attributes is
// not redacted, so the badge reads the same for every agent.
const whitelabUsername = computed(
  () => currentContact.value?.custom_attributes?.wl_username || ''
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const currentAccountSettings = computed(() => {
  const account = store.getters['accounts/getAccount'](accountId.value);
  return account?.settings || {};
});

const chatButtons = computed(() => {
  const buttons = [];
  for (let i = 1; i <= 3; i++) {
    const name = currentAccountSettings.value[`chat_button_${i}_name`];
    const url = currentAccountSettings.value[`chat_button_${i}_url`];
    if (name && url) {
      let processedUrl = url;
      if (currentContact.value) {
        processedUrl = url
          .replace('{phone}', currentContact.value.phone_number || '')
          .replace('{email}', currentContact.value.email || '')
          .replace('{name}', currentContact.value.name || '')
          .replace('{id}', currentContact.value.id || '');
      }
      buttons.push({ name, url: processedUrl });
    }
  }
  return buttons;
});

const canQuickCloseConversation = computed(() => {
  const status = String(currentChat.value?.status || '');
  return ['open', 'pending', 'snoozed'].includes(status);
});

// 判断是否需要使用下拉菜单（小于1024px且有2个或以上按钮）
const useDropdownMenu = computed(
  () => width.value < 1024 && chatButtons.value.length >= 2
);

// 切换下拉菜单
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

// 点击外部关闭下拉菜单
const closeDropdown = () => {
  isDropdownOpen.value = false;
};

// 监听点击事件，点击外部关闭下拉菜单
const handleClickOutside = event => {
  const dropdown = event.target.closest('.relative');
  if (!dropdown && isDropdownOpen.value) {
    closeDropdown();
  }
};

const closeChatDialogRef = ref(null);

const contactName = computed(() => {
  const name = currentChat.value?.meta?.sender?.name;
  return name || 'the customer';
});

const closeChatDescription = computed(() => {
  return `Are you sure you want to close the chat with <strong>${contactName.value}</strong>?`;
});

const quickCloseConversation = () => {
  if (!currentChat.value?.id || !canQuickCloseConversation.value) return;
  closeChatDialogRef.value?.open();
};

const confirmCloseConversation = async () => {
  if (isClosingConversation.value) return;
  isClosingConversation.value = true;
  try {
    await store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: wootConstants.STATUS_TYPE.RESOLVED,
      caseCloseType: 'complete',
    });
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  } finally {
    isClosingConversation.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div
    ref="conversationHeader"
    class="wlab-conversation-header flex flex-1 w-full min-w-0 px-4"
    :class="
      isQaMonitorRoute
        ? 'wlab-conversation-header--qa flex-col justify-center'
        : 'items-center justify-between'
    "
  >
    <template v-if="isQaMonitorRoute">
      <div class="w-full min-h-[20px] -mx-4 px-4" />

      <div class="flex items-center justify-start w-full min-h-[32px] pt-0.5">
        <BackButton
          v-if="showBackButton"
          :back-url="backButtonUrl"
          class="wlab-header-back-btn ltr:mr-2 rtl:ml-2"
        />
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="26"
          :status="currentContact.availability_status"
          hide-offline-status
          rounded-full
        />
        <div
          class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
        >
          <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
            <span
              class="text-sm font-medium truncate leading-tight text-n-slate-12"
            >
              {{ currentContact.name }}
            </span>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
              icon="warning"
            />
          </div>

          <div
            class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
          >
            <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
            <span v-if="isSnoozed" class="font-medium text-n-amber-10">
              {{ snoozedDisplayText }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div
        class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
      >
        <BackButton
          v-if="showBackButton"
          :back-url="backButtonUrl"
          class="wlab-header-back-btn ltr:mr-2 rtl:ml-2"
        />
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
          rounded-full
        />
        <div
          class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
        >
          <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
            <span
              class="text-sm font-medium truncate leading-tight text-n-slate-12"
            >
              {{ currentContact.name }}
            </span>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
              icon="warning"
            />
            <!-- Read straight from the contact record, so it costs no request
                 and survives the player returning through any channel.
                 flex-shrink-0 for the same reason as the icon above: the name
                 beside it truncates and would otherwise squash this away. -->
            <span
              v-if="whitelabUsername"
              v-tooltip="$t('CONVERSATION_SIDEBAR.WHITELAB.BADGE_TOOLTIP')"
              class="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md flex-shrink-0 max-w-[10rem] truncate bg-n-slate-3 text-n-slate-11"
            >
              <span class="i-ph-game-controller-bold text-xs shrink-0" />
              {{ whitelabUsername }}
            </span>
          </div>

          <div
            class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
          >
            <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
            <span v-if="isSnoozed" class="font-medium text-n-amber-10">
              {{ snoozedDisplayText }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="flex flex-row items-center justify-end flex-shrink-0 gap-2 header-actions-wrap"
      >
        <button
          v-if="canQuickCloseConversation && !isSmallScreen"
          class="wlab-close-chat-btn"
          :disabled="isClosingConversation"
          title="Close chat"
          @click="quickCloseConversation"
        >
          <fluent-icon icon="dismiss" size="14" />
          <span class="hidden lg:inline">Close</span>
        </button>
        <SLACardLabel
          v-if="hasSlaPolicyId"
          :chat="chat"
          show-extended-info
          :parent-width="width"
          class="hidden md:flex"
        />

        <!-- 大屏幕或只有一个按钮：直接显示按钮 -->
        <template v-if="!useDropdownMenu || chatButtons.length === 1">
          <a
            v-for="(button, index) in chatButtons"
            :key="index"
            :href="button.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center justify-center h-8 px-2 lg:px-3 text-xs lg:text-sm font-medium transition-colors rounded-lg bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-12"
            :title="button.name"
          >
            <fluent-icon icon="link" size="14" class="lg:mr-1" />
            <span class="hidden lg:inline">{{ button.name }}</span>
          </a>
        </template>

        <!-- 小屏幕：下拉菜单 -->
        <div v-else-if="chatButtons.length > 0" class="relative">
          <button
            class="inline-flex items-center justify-center h-8 px-2 text-sm font-medium transition-colors rounded-lg bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-12"
            :title="$t('CONVERSATION.CUSTOM_ACTIONS')"
            @click="toggleDropdown"
          >
            <fluent-icon icon="link" size="16" />
            <fluent-icon
              icon="chevron-down"
              size="12"
              class="ml-1 transition-transform"
              :class="{ 'rotate-180': isDropdownOpen }"
            />
          </button>

          <!-- 下拉菜单内容 -->
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <div
              v-if="isDropdownOpen"
              class="absolute right-0 z-50 mt-2 w-48 rounded-md shadow-lg bg-n-slate-1 ring-1 ring-black ring-opacity-5"
              @click.stop
            >
              <div class="py-1">
                <a
                  v-for="(button, index) in chatButtons"
                  :key="index"
                  :href="button.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center px-4 py-2 text-sm text-n-slate-12 hover:bg-n-slate-3 transition-colors"
                  @click="closeDropdown"
                >
                  <fluent-icon icon="link" size="16" class="mr-2" />
                  {{ button.name }}
                </a>
              </div>
            </div>
          </transition>
        </div>

        <!-- Copy chat link button -->
        <button
          class="wlab-copy-link-btn"
          title="Copy chat link"
          @click="copyConversationLink"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="18"
            height="18"
          >
            <g
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              clip-path="url(#link_svg__a)"
            >
              <path
                d="m9 15 6-6M11 6l.463-.536a5 5 0 0 1 7.071 7.072L18 13M13 18l-.397.534a5.07 5.07 0 0 1-7.127 0 4.97 4.97 0 0 1 0-7.071L6 11"
              />
            </g>
            <defs>
              <clipPath id="link_svg__a">
                <path fill="#fff" d="M0 0h24v24H0z" />
              </clipPath>
            </defs>
          </svg>
        </button>

        <!-- Copied toast notification -->
        <transition name="fade">
          <div v-if="showCopiedToast" class="wlab-copied-toast">
            Link copied!
          </div>
        </transition>

        <MoreActions :conversation-id="currentChat.id" />
      </div>
    </template>

    <ConfirmActionDialog
      ref="closeChatDialogRef"
      title="Close this chat?"
      :description="closeChatDescription"
      confirm-label="Close chat"
      cancel-label="Cancel"
      :confirm-destructive="true"
      @confirm="confirmCloseConversation"
    />
  </div>
</template>

<style scoped>
.wlab-conversation-header {
  height: 56px;
  min-height: 56px;
  background-color: var(--wlab-header-bg);
  border-bottom: 1px solid var(--wlab-header-border);
}

.wlab-conversation-header--qa {
  height: 56px;
  min-height: 56px;
}

.wlab-copy-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 4px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--wlab-text-secondary, #475569);
  cursor: pointer;
}

.wlab-close-chat-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--wlab-text-secondary, #475569);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.wlab-close-chat-btn:hover {
  background: var(--wlab-bg-hover, #f1f5f9);
  color: var(--wlab-text-primary, #0f172a);
}

.wlab-close-chat-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.wlab-copy-link-btn:hover {
  background: var(--wlab-bg-hover, #f1f5f9);
  color: var(--wlab-text-primary, #0f172a);
}

.wlab-copied-toast {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 15000;
  padding: 8px 16px;
  border-radius: 8px;
  background: #131317;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 6px 20px rgba(19, 19, 23, 0.3);
  white-space: nowrap;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .wlab-conversation-header {
    padding-right: 12px;
    padding-left: 12px;
  }

  .wlab-conversation-header :deep(.wlab-header-back-btn) {
    min-width: 20px;
    font-size: 0 !important;
    line-height: 0 !important;
  }

  .wlab-conversation-header :deep(.wlab-header-back-btn i) {
    margin-left: -2px;
    font-size: 18px !important;
  }

  .header-actions-wrap {
    gap: 4px;
  }
}
</style>
